/**
 * Fantasy Console 0 - Color Palette Component
 *
 * 4×4 grid of 16 PICO-8 colors for selecting the drawing color.
 */

import { useCallback } from 'react'
import { ColorIndex } from '@fc0/types'
import { PICO8_PALETTE } from '../../utils/spriteUtils'
import styles from './ColorPalette.module.css'
import editorStyles from '../../styles/editor.module.css'

interface ColorPaletteProps {
  selectedColor: ColorIndex
  onSelectColor: (color: ColorIndex) => void
}

export function ColorPalette({
  selectedColor,
  onSelectColor,
}: ColorPaletteProps) {
  const handleColorClick = useCallback(
    (color: ColorIndex) => {
      onSelectColor(color)
    },
    [onSelectColor],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, color: ColorIndex) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onSelectColor(color)
      }
    },
    [onSelectColor],
  )

  return (
    <div className={`${editorStyles.panelContainer} ${styles.container}`}>
      <div className={editorStyles.panelHeader}>
        <span className={editorStyles.panelLabel}>Colors</span>
        <span className={editorStyles.panelInfo}>#{selectedColor}</span>
      </div>
      <div className={styles.grid}>
        {PICO8_PALETTE.map((color, index) => {
          const colorIndex = index as ColorIndex
          const isSelected = colorIndex === selectedColor
          const isLight = [6, 7, 9, 10, 11, 14, 15].includes(index)

          return (
            <button
              key={index}
              className={`${styles.color} ${isSelected ? styles.selected : ''}`}
              style={{ backgroundColor: color }}
              onClick={() => handleColorClick(colorIndex)}
              onKeyDown={(e) => handleKeyDown(e, colorIndex)}
              title={`Color ${index}${getShortcutHint(index)}`}
              aria-label={`Color ${index}`}
              aria-pressed={isSelected}
            >
              <span className={`${styles.index} ${isLight ? styles.dark : ''}`}>
                {index.toString(16).toUpperCase()}
              </span>
            </button>
          )
        })}
      </div>
      <div className={styles.preview}>
        <div
          className={styles.previewSwatch}
          style={{ backgroundColor: PICO8_PALETTE[selectedColor] }}
        />
        <span className={styles.previewLabel}>
          {PICO8_PALETTE[selectedColor]}
        </span>
      </div>
    </div>
  )
}

function getShortcutHint(index: number): string {
  if (index < 10) {
    return ` (${index})`
  }
  return ` (Shift+${index - 10})`
}
